/**
 * HubApp — top-level router for hub.usetenon.com.
 * Mounted by App.jsx when window.location.hostname starts with "hub.".
 * Owns its own browser router, separate from the main app's; auth state lives
 * in localStorage.ml_hub_token (not ml_token).
 *
 * HUB-REDESIGN P1: switched NAV to Operate (Overview/Business/Sales/
 * Customers/Tenants/Support) + Platform (Engineering/Settings) IA.
 * HUB-REDESIGN P4: legacy queue-led routes (/queue /shipped /errors /logs
 * /events /health) folded into the combined /engineering page; their
 * components were deleted.
 * HUB-REDESIGN P5: Sales/Customers/Settings placeholders replaced with
 * real bento pages; Business reshelled to design; /api-keys folded into
 * Settings (still reachable directly).
 */
import React, { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { initPostHog, posthogCapture } from './analytics'
import HubLoginPage from './HubLoginPage'
import HubLayout from './HubLayout'
import HubTenantsPage from './HubTenantsPage'
import HubTenantDetailPage from './HubTenantDetailPage'
import HubAccountDetailPage from './HubAccountDetailPage'
import HubAgentsPage from './HubAgentsPage'
import HubAgentDetailPage from './HubAgentDetailPage'
import HubBusinessPage from './HubBusinessPage'
import HubSalesPage from './HubSalesPage'
import HubCustomersPage from './HubCustomersPage'
import HubSettingsPage from './HubSettingsPage'
import HubApiKeysPage from './HubApiKeysPage'
import HubSupportPage from './HubSupportPage'
import HubOverviewPage from './HubOverviewPage'
import HubMissionControlPage from './HubMissionControlPage'
import HubReleaseNotesPage from './HubReleaseNotesPage'
import HubEngineeringPage from './HubEngineeringPage'
import HubEmailsPage from './HubEmailsPage'
import { getHubToken } from './api'

function RequireHubAuth({ children }: { children: React.ReactNode }) {
  if (!getHubToken()) return <Navigate to="/auth/login" replace />
  return <>{children}</>
}

/** Sets <title> per route as "<Label> · Hub · Tenon". Renders nothing.
 *  Must live inside BrowserRouter (uses useLocation). */
function HubTitleSetter() {
  useDocumentTitle('Hub')
  return null
}

/** Fires a PostHog $pageview on every SPA navigation. */
function HubPageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    posthogCapture('$pageview', { path: location.pathname })
  }, [location.pathname])
  return null
}

export default function HubApp() {
  useEffect(() => {
    fetch('/hub/config')
      .then(r => r.json())
      .then((cfg: { posthog_key: string; posthog_host: string }) => {
        initPostHog(cfg.posthog_key, cfg.posthog_host)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      posthogCapture('client_error', { message: e.message, filename: e.filename, lineno: e.lineno })
    }
    const onUnhandled = (e: PromiseRejectionEvent) => {
      posthogCapture('client_error', { message: String(e.reason), type: 'unhandled_rejection' })
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandled)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandled)
    }
  }, [])

  return (
    <BrowserRouter>
      <HubTitleSetter />
      <HubPageViewTracker />
      <Routes>
        <Route path="/auth/login" element={<HubLoginPage />} />
        <Route element={<RequireHubAuth><HubLayout /></RequireHubAuth>}>
          <Route index element={<Navigate to="/overview" replace />} />
          <Route path="/mission-control" element={<HubMissionControlPage />} />
          <Route path="/release-notes" element={<HubReleaseNotesPage />} />
          <Route path="/overview" element={<HubOverviewPage />} />
          <Route path="/business" element={<HubBusinessPage />} />
          <Route path="/sales" element={<HubSalesPage />} />
          <Route path="/customers" element={<HubCustomersPage />} />
          <Route path="/tenants" element={<HubTenantsPage />} />
          <Route path="/tenants/:id" element={<HubTenantDetailPage />} />
          <Route path="/accounts/:id" element={<HubAccountDetailPage />} />
          <Route path="/agents" element={<HubAgentsPage />} />
          <Route path="/agents/:id/*" element={<HubAgentDetailPage />} />
          <Route path="/support" element={<HubSupportPage />} />
          <Route path="/engineering" element={<HubEngineeringPage />} />
          <Route path="/emails" element={<HubEmailsPage />} />
          <Route path="/settings" element={<HubSettingsPage />} />

          {/* Legacy queue-led routes redirect into the combined engineering
              page so any bookmarked URL still resolves. */}
          <Route path="/queue" element={<Navigate to="/engineering" replace />} />
          <Route path="/shipped" element={<Navigate to="/engineering" replace />} />
          <Route path="/errors" element={<Navigate to="/engineering" replace />} />
          <Route path="/logs" element={<Navigate to="/engineering" replace />} />
          <Route path="/events" element={<Navigate to="/engineering" replace />} />
          <Route path="/health" element={<Navigate to="/engineering" replace />} />

          {/* Super-admin API key audit — discoverable from the Settings page
              link strip (P5). Still reachable via direct URL for muscle memory. */}
          <Route path="/api-keys" element={<HubApiKeysPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
